import axios from "axios";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api/v2`;
const ACCESS_KEY = "vt_access_token";
const REFRESH_KEY = "vt_refresh_token";
const REMEMBER_KEY = "vt_remember";

// "Remember me" decides WHERE the tokens live: localStorage survives browser
// restarts, sessionStorage clears when the tab/browser closes. Default to
// localStorage (remembered) so existing logged-in sessions keep working.
function getStorage() {
  return localStorage.getItem(REMEMBER_KEY) === "0" ? sessionStorage : localStorage;
}

function getToken(key) {
  return localStorage.getItem(key) || sessionStorage.getItem(key);
}

export const apiClient = axios.create({ baseURL: API_BASE });

// Stable per-tab id so realtime can skip ONLY the originating browser session
// (not every session of the same admin). Sent on every request; the backend
// echoes it back as the event's `by`, and useRealtime ignores events it caused.
export const CLIENT_SESSION_ID =
  (typeof crypto !== "undefined" && crypto.randomUUID)
    ? crypto.randomUUID()
    : `s_${Date.now()}_${Math.random().toString(36).slice(2)}`;

apiClient.interceptors.request.use((config) => {
  const token = getToken(ACCESS_KEY);
  if (token) config.headers.Authorization = `Bearer ${token}`;
  config.headers["X-Client-Session"] = CLIENT_SESSION_ID;
  return config;
});

let isRefreshing = false;
let pendingResolvers = [];

apiClient.interceptors.response.use(
  (res) => res,
  async (error) => {
    const config = error?.config;
    const status = error?.response?.status;
    const isAuthCall = config?.url?.includes("/auth/login") || config?.url?.includes("/auth/refresh");

    if (status === 401 && config && !config._retried && !isAuthCall) {
      config._retried = true;
      const refreshToken = getToken(REFRESH_KEY);
      if (!refreshToken) {
        clearSession();
        redirectToLogin();
        return Promise.reject(error);
      }
      try {
        if (!isRefreshing) {
          isRefreshing = true;
          const { data } = await axios.post(`${API_BASE}/auth/refresh`, {
            refresh_token: refreshToken,
          });
          const storage = getStorage();
          storage.setItem(ACCESS_KEY, data.access_token);
          storage.setItem(REFRESH_KEY, data.refresh_token);
          isRefreshing = false;
          pendingResolvers.forEach((resolve) => resolve(data.access_token));
          pendingResolvers = [];
        }
        const token = await new Promise((resolve) => {
          const current = getToken(ACCESS_KEY);
          if (!isRefreshing) resolve(current);
          else pendingResolvers.push(resolve);
        });
        config.headers.Authorization = `Bearer ${token}`;
        return apiClient(config);
      } catch (refreshError) {
        isRefreshing = false;
        pendingResolvers.forEach((resolve) => resolve(null));
        pendingResolvers = [];
        // Only wipe saved tokens when the server actually rejected the
        // refresh token (401). A network blip or transient 5xx while
        // refreshing must NOT log out a "Remember me" user.
        if (refreshError?.response?.status === 401) {
          clearSession();
          redirectToLogin();
        }
        return Promise.reject(refreshError);
      }
    }
    return Promise.reject(error);
  }
);

export function clearSession() {
  localStorage.removeItem(ACCESS_KEY);
  localStorage.removeItem(REFRESH_KEY);
  localStorage.removeItem(REMEMBER_KEY);
  sessionStorage.removeItem(ACCESS_KEY);
  sessionStorage.removeItem(REFRESH_KEY);
}

export function saveSession({ access_token, refresh_token }, remember = true) {
  clearSession();
  localStorage.setItem(REMEMBER_KEY, remember ? "1" : "0");
  const storage = remember ? localStorage : sessionStorage;
  storage.setItem(ACCESS_KEY, access_token);
  storage.setItem(REFRESH_KEY, refresh_token);
}

export function hasSession() {
  return Boolean(getToken(ACCESS_KEY));
}

function redirectToLogin() {
  const path = window.location.pathname;
  const loginPath = path.startsWith("/driver") ? "/driver/login"
    : path.startsWith("/customer") ? "/customer/login"
    : path.startsWith("/pos") ? "/pos/login"
    : "/login";
  if (window.location.pathname !== loginPath) {
    window.location.href = loginPath;
  }
}

// Turns FastAPI error payloads (string detail, or pydantic 422 detail arrays)
// into a single plain-English message for staff to read.
export function getApiErrorMessage(error, fallback = "Something went wrong. Please try again.") {
  const detail = error?.response?.data?.detail;
  if (typeof detail === "string" && detail.trim()) return detail;
  if (Array.isArray(detail) && detail.length) {
    return detail
      .map((d) => {
        const field = Array.isArray(d?.loc) ? d.loc[d.loc.length - 1] : null;
        return field ? `${field}: ${d.msg}` : d.msg;
      })
      .filter(Boolean)
      .join("; ");
  }
  if (error?.message === "Network Error") {
    return "Cannot reach the server. Check your connection and try again.";
  }
  if (error?.code === "ECONNABORTED") return "The request timed out. Please try again.";
  const status = error?.response?.status;
  if (status === 502 || status === 503 || status === 504) {
    return "The server is temporarily unavailable. Please try again in a moment.";
  }
  return fallback;
}

// Maps FastAPI/pydantic 422 `detail` arrays into a { fieldName: message } dict so
// forms can show the error right under the offending input.
export function getFieldErrors(error) {
  const detail = error?.response?.data?.detail;
  const fields = {};
  if (Array.isArray(detail)) {
    detail.forEach((d) => {
      const field = Array.isArray(d?.loc) ? d.loc[d.loc.length - 1] : null;
      if (field) fields[field] = d.msg;
    });
  }
  return fields;
}
